'use client';

import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { Notification } from '@/lib/notifications/service';

// A "chat banner" is a critical, action-required notification that the user
// can resolve inline above the composer. We surface payment_required (and
// future settlement-failure) prompts here when the user is actively viewing
// the matching chat. The overlay (CriticalNotificationOverlay) is responsible
// for routing into this provider only when the chat is on screen.
//
// "On screen" is the route OR an open chat window (2026-11-03). In the
// workspace a chat is a window on `/workspace/<id>`, so its id is never in the
// URL, and a prompt for the chat a person was looking at always came as a page
// toast in the corner (the dev browser pass of that day). Every mounted
// `useChatBanner(chatId)` (ChatPaymentBanner, inside each ChatView) registers
// its chat here; `openChatsVersion` changes when that set does, so the overlay
// can move a prompt into a window that opens, or back out of one that closes.

export interface ChatBanner {
  notification: Notification;
}

interface ChatBannerContextValue {
  banners: Map<string, ChatBanner>;          // chatId -> latest banner
  pushBanner: (chatId: string, banner: ChatBanner) => void;
  dismissBanner: (chatId: string) => void;
  dismissNotification: (notificationId: string) => void;
  /** Mark a chat as shown in a mounted chat view; returns the unmark. */
  registerOpenChat: (chatId: string) => () => void;
  isChatOpen: (chatId: string) => boolean;
  /** Changes whenever the set of open chats does. */
  openChatsVersion: number;
}

const ChatBannerContext = createContext<ChatBannerContextValue | null>(null);

export function ChatBannerProvider({ children }: { children: ReactNode }) {
  const [banners, setBanners] = useState<Map<string, ChatBanner>>(new Map());
  // chatId -> mounted views showing it (one chat can be open in two windows).
  const openChatsRef = useRef<Map<string, number>>(new Map());
  const [openChatsVersion, setOpenChatsVersion] = useState(0);

  const registerOpenChat = useCallback((chatId: string) => {
    const open = openChatsRef.current;
    open.set(chatId, (open.get(chatId) ?? 0) + 1);
    setOpenChatsVersion((v) => v + 1);
    return () => {
      const n = (open.get(chatId) ?? 1) - 1;
      if (n > 0) open.set(chatId, n);
      else open.delete(chatId);
      setOpenChatsVersion((v) => v + 1);
    };
  }, []);

  const isChatOpen = useCallback((chatId: string) => openChatsRef.current.has(chatId), []);

  const pushBanner = useCallback((chatId: string, banner: ChatBanner) => {
    setBanners((prev) => {
      const next = new Map(prev);
      next.set(chatId, banner);
      return next;
    });
  }, []);

  const dismissBanner = useCallback((chatId: string) => {
    setBanners((prev) => {
      if (!prev.has(chatId)) return prev;
      const next = new Map(prev);
      next.delete(chatId);
      return next;
    });
  }, []);

  const dismissNotification = useCallback((notificationId: string) => {
    setBanners((prev) => {
      let mutated = false;
      const next = new Map(prev);
      for (const [chatId, banner] of prev) {
        if (banner.notification.id === notificationId) {
          next.delete(chatId);
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, []);

  const value = useMemo<ChatBannerContextValue>(
    () => ({ banners, pushBanner, dismissBanner, dismissNotification, registerOpenChat, isChatOpen, openChatsVersion }),
    [banners, pushBanner, dismissBanner, dismissNotification, registerOpenChat, isChatOpen, openChatsVersion],
  );

  return <ChatBannerContext.Provider value={value}>{children}</ChatBannerContext.Provider>;
}

export function useChatBanner(chatId: string | null | undefined) {
  const ctx = useContext(ChatBannerContext);
  const register = ctx?.registerOpenChat;
  useEffect(() => {
    if (!register || !chatId) return;
    return register(chatId);
  }, [register, chatId]);
  if (!ctx) {
    return {
      banner: undefined as ChatBanner | undefined,
      dismiss: () => {},
    };
  }
  const banner = chatId ? ctx.banners.get(chatId) : undefined;
  return {
    banner,
    dismiss: () => chatId && ctx.dismissBanner(chatId),
  };
}

export function useChatBannerControls() {
  const ctx = useContext(ChatBannerContext);
  if (!ctx) throw new Error('useChatBannerControls must be used inside ChatBannerProvider');
  return ctx;
}
